import { ReactNode } from 'react';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import classNames from 'classnames';

import { Box } from '../../Box';
import { Icon } from '../../Icon';
import { Label } from '../common';

import { useStyles } from './RadioInputComplexLabel.styles';

/**
 * Свойства компонента {@link RadioInputComplexLabel}
 *
 * @typeParam T - Тип метки.
 */
interface RadioInputProps<T> {
  /**
   * Массив элементов, каждый из которых содержит метку и значение.
   */
  items: {
    label: T;
    value: string | number;
  }[];

  /**
   * Текущее выбранное значение.
   */
  value: string | number | undefined;

  /**
   * Функция обратного вызова, которая вызывается при изменении выбранного значения.
   */
  onChange: (value?: string | number) => void;

  /**
   * Функция для рендера метки каждого элемента.
   */
  render: (item: T) => ReactNode;

  /**
   * Функция для рендера дополнительного контента под каждым элементом.
   */
  renderFooter?: (item: T, isSelected: boolean) => ReactNode;

  /**
   * Вариант стиля радио-кнопок.
   *
   * @defaultValue `'primary'`
   */
  variant?: 'primary' | 'secondary';

  /**
   * Текстовая метка для всей группы радио-кнопок.
   */
  label?: string;

  /**
   * Флаг обязательности выбора.
   */
  required?: boolean;

  /**
   * Разрешить снятие выбора при повторном клике на выбранный элемент.
   */
  allowClear?: boolean;
}

/**
 * Компонент RadioInputComplexLabel представляет собой настраиваемый радио-ввод с комплексными метками.
 * Он позволяет рендерить пользовательские компоненты меток для каждого варианта радио.
 *
 * @public
 * @param items - Массив элементов, каждый из которых содержит метку и значение.
 * @param value - Текущее выбранное значение.
 * @param onChange - Функция обратного вызова, которая вызывается при изменении выбранного значения.
 * @param render - Функция для рендера метки каждого элемента.
 * @param renderFooter - Функция для рендера дополнительного контента под каждым элементом.
 * @param variant - Вариант стиля радио-кнопок.
 * @param label - Текстовая метка для всей группы радио-кнопок.
 * @param required - Флаг обязательности выбора.
 * @param allowClear - Разрешить снятие выбора при повторном клике на выбранный элемент.
 * @param rest - Остальные пропсы, передаваемые в {@link RadioGroup}.
 * @returns Компонент RadioInputComplexLabel.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link RadioInputProps}
 */
export const RadioInputComplexLabel = <T,>({
  items,
  value,
  onChange,
  render,
  renderFooter,
  variant = 'primary',
  label,
  required = false,
  allowClear,
  ...rest
}: RadioInputProps<T>) => {
  const { classes } = useStyles();

  return (
    <Box>
      {label && <Label label={label} required={required} />}
      <FormControl component="fieldset" classes={{ root: classes.formRoot }}>
        <RadioGroup
          {...rest}
          row={false}
          value={value}
          onChange={(e) => {
            e.stopPropagation();
            onChange(e.target.value);
          }}
          className="aqa_radioinput_block"
        >
          <Box display="flex" flexDirection="column" gap="12">
            {items.map((item) => (
              <Box
                key={item.value}
                display="flex"
                flexDirection="column"
                gap="12"
                bgcolor="bg.light.primary"
                className={classNames(
                  classes.itemBox,
                  variant === 'secondary'
                    ? classes.secondaryItem
                    : classes.primaryItem,
                  item.value === value && classes.checkedItem,
                )}
              >
                <FormControlLabel
                  value={item.value}
                  classes={{
                    root: classes.labelRoot,
                  }}
                  control={
                    <Radio
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      onClick={() => {
                        if (allowClear && item.value === value) {
                          onChange(undefined);
                        }
                      }}
                      classes={{
                        root: classes.inputRoot,
                      }}
                      icon={
                        <Icon
                          name="radio_off"
                          size="24"
                          color="stroke.primary"
                        />
                      }
                      checkedIcon={<Icon name="radio_on" size="24" />}
                    />
                  }
                  label={render(item.label)}
                  labelPlacement="end"
                />
                {renderFooter?.(item.label, item.value === value)}
              </Box>
            ))}
          </Box>
        </RadioGroup>
      </FormControl>
    </Box>
  );
};
